import {
    type RefObject,
    useMemo,
    useState,
    useEffect,
    useRef,
    type ImgHTMLAttributes,
    type HTMLAttributes,
} from 'react';
import clsx from 'clsx';
import sortBy from 'Utils/sortBy';
import { RenderMarkdown } from 'MarkdownComponents';
import {
    type StreamSnakeCasedAttrs,
    type LinkToLessonInStreamWithContentAccessInfo,
    type StreamIguanaClass,
    type ContentAccessHelperClass,
    type LinkToLessonInStream,
    lessonsApi,
} from 'Lessons';
import FrontRoyalSpinner from 'FrontRoyalSpinner';
import { IconButton } from '@mui/material';
import { Replay } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Transition } from '@headlessui/react';
import { useSelector } from 'react-redux';
import { TutorBotMessageContent } from 'TutorBotMessageContent';
import { PRETEND_MESSAGE_SPINNER_DURATION_MS } from 'TutorBotConversation';
import { angularInjectorProvider } from 'Injector';
import {
    type SourceMetadata,
    type DisplayableNonLessonFrameSourceLocation,
    type SourceLink,
} from '../types/AnswerSource.types';
import {
    isLessonFrameSourceLocation,
    isDisplayableNonLessonFrameSourceLocation,
    isLinkToLessonInStreamWithContentAccessInfo,
    getUrlForLink,
    isHelpscoutSourceLocation,
} from '../utils/sourceLocationHelpers';
import { type ChatMessage, type RealAiMessage } from '../types/ChatMessage.types';
import { type StudentMessageEvaluation } from '../types/OtherInternalTypes.types';
import { getStreamingAiMessage } from '../redux/chat';
import { isHumanMessage, isPretendAiMessage, isRealAiMessage, getMessageSources } from '../utils/ChatMessage';
import { addFootnotesToMessage } from '../utils/addFootnotesToMessage';

import { MessageEvaluations } from './MessageEvaluations';
import MessageSources from './MessageSources';
import { MarkdownStyledLink } from './MarkdownStyledLink';
import { helpScoutCollectionTitle } from '../utils/helpScoutCollectionTitle';
import { formatMathInMessage } from '../utils/formatMathInMessage';

type Props = {
    message: ChatMessage;
    showPretendLoadingSpinner?: boolean;
    className?: string;
    onThumbClick?: (messageId: string, label: StudentMessageEvaluation['label']) => void;
    retryClick?: () => void;
    messageRef?: RefObject<HTMLDivElement>;
};

const { useGetLinksToLessonsInStreamsQuery } = lessonsApi;

const LoadingSpinner = () => <FrontRoyalSpinner className="no-top-margin no-delay static-height" />;

const ErrorMessage = ({ message, retryClick }: { message: ChatMessage; retryClick: () => void }) => (
    <IconButton onClick={retryClick} disableRipple sx={{ fontSize: '1rem' }}>
        {message.content}
        <Replay className="text-green" />
    </IconButton>
);

const LoadingStatusMessage = ({ status }: { status: RealAiMessage['status'] }) => {
    const { t } = useTranslation('back_royal');
    // Falls back to empty string for unrecognized keys so older clients degrade gracefully
    // when the server emits new status values before a client update ships.
    const statusText = t([`chat.chat.${status?.key}`, ''], status?.params ?? {}) ?? null;
    return (
        <Transition
            as="div"
            show={!!statusText}
            enter="transition-all duration-500 ease-out"
            enterFrom="opacity-0 -translate-x-1"
            enterTo="opacity-100 translate-x-0"
            leave="transition-all duration-200 ease-in"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
        >
            <span className="text-tutorbot-chat opacity-60">{statusText}</span>
        </Transition>
    );
};

// Custom Markdown components to override the default ones:

// see comment down inside the definition of preparedMessageContent
const ImgWithStreamingCursorSupport = ({ alt, ...rest }: ImgHTMLAttributes<HTMLImageElement>) => {
    if (alt === 'StreamingCursor') {
        return (
            <span>
                &nbsp;
                <span className="-mb-[2px] inline-block h-[1em] w-[0.5em] bg-tutorbot-chat opacity-50" />
            </span>
        );
    }

    return <img {...rest} alt={alt} />;
};

const MarkdownParagraphHumanMessage = ({ children }: HTMLAttributes<HTMLElement>) => (
    <p className="mb-3 last:mb-0">{children}</p>
);

// We must override the color for certain elements that match differently styled selectors in scss
const MarkdownParagraphAiMessage = ({ children }: HTMLAttributes<HTMLElement>) => (
    <p className="mb-3 text-black last:mb-0">{children}</p>
);

function getLessonIdForSource({
    source,
    lessonLinksForUser,
}: {
    source: SourceMetadata;
    lessonLinksForUser: Record<string, LinkToLessonInStream>;
}) {
    // I had to use this for loop to get around typescript errors. I'm sure there's a cleaner way
    for (let i = 0; i < source.locations.length; i++) {
        const loc = source.locations[i];
        if (isLessonFrameSourceLocation(loc)) {
            const lessonId = loc.lessonId;
            if (lessonLinksForUser[lessonId]) return lessonId;
        }
    }

    return null;
}

// determine whether the user can launch this stream to know whether to display the link as locked or not
function addContentAccessInfo(linkToLessonInStream: LinkToLessonInStream): LinkToLessonInStreamWithContentAccessInfo {
    const injector = angularInjectorProvider.requireInjector();
    const Stream = injector.get('Lesson.Stream') as StreamIguanaClass;
    const ContentAccessHelper = injector.get('ContentAccessHelper') as ContentAccessHelperClass;

    // This is underhanded, but we're working with the intentionally lightweight attributes returned from the
    // getLinksToLessonsInStreams query.  We don't have all the attributes needed to create a full Stream object,
    // but we have just enough for the ContentAccessHelper logic to work.
    const streamAttrs = {
        id: linkToLessonInStream.streamAttributes.id,
        title: linkToLessonInStream.streamAttributes.title,
        locale_pack: linkToLessonInStream.streamAttributes.localePack,
    };
    const streamObject = Stream.new(streamAttrs as unknown as StreamSnakeCasedAttrs);
    const accessHelper = ContentAccessHelper.new(streamObject);

    const linkToLessonInStreamWithContentAccessInfo: LinkToLessonInStreamWithContentAccessInfo = {
        ...linkToLessonInStream,
        canLaunch: accessHelper.canLaunch,
        reasonMessage: accessHelper.canLaunch ? null : accessHelper.reasonMessage,
    };
    return linkToLessonInStreamWithContentAccessInfo;
}

// In the case of lesson frame locations, the link for a source is an instance of LinkToLessonInStream,
// which we embellish with info about whether the user can launch the stream.
// In the case of other locations, the link is just the location itself, since it has a url property.
function getLinksFromSource(
    source: SourceMetadata,
    lessonLinksForUser: Record<string, LinkToLessonInStream>,
): (LinkToLessonInStreamWithContentAccessInfo | DisplayableNonLessonFrameSourceLocation)[] {
    const lessonId = getLessonIdForSource({ source, lessonLinksForUser });
    const linkToLessonInStream = lessonId && lessonLinksForUser[lessonId];
    if (linkToLessonInStream) {
        return [addContentAccessInfo(linkToLessonInStream)];
    }

    return source.locations.filter(loc =>
        isDisplayableNonLessonFrameSourceLocation(loc),
    ) as DisplayableNonLessonFrameSourceLocation[];
}

function getSortKeyForLink(sourceLink: SourceLink) {
    const link = sourceLink.link;
    if (isLinkToLessonInStreamWithContentAccessInfo(link)) {
        return `${link.canLaunch ? 0 : 1}${link.streamTitle}${link.lessonIndexInStream}`;
    }
    if (isHelpscoutSourceLocation(link)) return `${helpScoutCollectionTitle(link.collectionId)}${link.title}`;
    if (isDisplayableNonLessonFrameSourceLocation(link)) return link.title;
    return ''; // you can't actually get here
}

// oxlint-disable-next-line max-lines-per-function
export function Message({ message, className, retryClick, showPretendLoadingSpinner = false, messageRef }: Props) {
    const { data: lessonLinksForUser } = useGetLinksToLessonsInStreamsQuery();

    // We have some hard-coded messages that we'd like to present as if they were being
    // generated via Tutorbot - in those cases, we can use the pretendToLoadMessage property
    // here to "pretend" that we're spending time retrieving the message.
    const [isPretendLoading, setIsPretendLoading] = useState(showPretendLoadingSpinner);
    const isLoadingBotMessage = message.role === 'ai' && !message.hasError && !message.complete && !message.content;
    const streamingAiMessage = useSelector(getStreamingAiMessage);
    const isStreamingAiMessage = streamingAiMessage?.id === message.id;
    const [isStreamingDelay, setIsStreamingDelay] = useState(false);
    const streamingDelayTimeoutId = useRef<NodeJS.Timeout | null>(null);
    const isLoading = isPretendLoading || isLoadingBotMessage || isStreamingDelay;

    useEffect(() => {
        let timeoutId: NodeJS.Timeout;

        if (isPretendAiMessage(message) && isPretendLoading) {
            timeoutId = setTimeout(() => {
                setIsPretendLoading(false);
            }, PRETEND_MESSAGE_SPINNER_DURATION_MS);
        }

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [message, isPretendLoading]);

    // Show the loading spinner if it's been more than 2 seconds since the last token was added to the message
    useEffect(() => {
        setIsStreamingDelay(false);

        if (streamingDelayTimeoutId.current) {
            clearTimeout(streamingDelayTimeoutId.current);
        }

        const timeoutId: NodeJS.Timeout = setTimeout(() => {
            if (isStreamingAiMessage) {
                setIsStreamingDelay(true);
            }
        }, 2000);

        streamingDelayTimeoutId.current = timeoutId;

        return () => {
            clearTimeout(timeoutId);
        };
    }, [isStreamingAiMessage, message.content]);

    const isFromTutorBot = message.role === 'ai';
    const isError = message.id === 'error-message' && !!retryClick;

    const shouldShowEvaluations = !isPretendAiMessage(message) && isFromTutorBot && !isLoadingBotMessage;

    const sourceLinks: SourceLink[] = useMemo(() => {
        if (!lessonLinksForUser) {
            return [];
        }

        const sources = getMessageSources(message);

        const unsortedLinks: Record<string, SourceLink> = {};
        sources.forEach((source: SourceMetadata) => {
            getLinksFromSource(source, lessonLinksForUser).forEach(link => {
                const url = getUrlForLink(link);
                if (!url) return;
                if (!unsortedLinks[url]) {
                    unsortedLinks[url] = {
                        sourceIds: [],
                        link,
                    };
                }
                if (source.id) {
                    unsortedLinks[url].sourceIds.push(source.id);
                }
            });
        });

        return sortBy(Object.values(unsortedLinks), (link: SourceLink) => getSortKeyForLink(link)) as SourceLink[];
    }, [message, lessonLinksForUser]);

    const preparedMessageContent = useMemo(() => {
        if (!message.content) return null;
        const footnoteReferences = isRealAiMessage(message) ? message.footnoteReferences : {};
        let messageContent = addFootnotesToMessage(message.content, footnoteReferences, sourceLinks);
        messageContent = formatMathInMessage(messageContent);

        // In order to get the cursor to display inline, it has to be injected into the markdown. We do
        // this by hacking the handling of the image tag
        const showCursor = isStreamingAiMessage && !message.complete;
        if (showCursor) {
            messageContent += '![StreamingCursor](StreamingCursor)';
        }

        return messageContent;
    }, [message, sourceLinks, isStreamingAiMessage]);

    return (
        <div className={clsx(' flex w-full', className)} data-testid="message" ref={messageRef}>
            <TutorBotMessageContent
                isHumanMessage={isHumanMessage(message)}
                isLoading={isLoading}
                contentType={message.contentType}
            >
                {isError ? (
                    <ErrorMessage retryClick={retryClick} message={message} />
                ) : (
                    <div>
                        {!isPretendLoading && preparedMessageContent && (
                            <RenderMarkdown
                                components={{
                                    a: MarkdownStyledLink,
                                    img: ImgWithStreamingCursorSupport,
                                    p: isHumanMessage(message)
                                        ? MarkdownParagraphHumanMessage
                                        : MarkdownParagraphAiMessage,
                                }}
                            >
                                {preparedMessageContent}
                            </RenderMarkdown>
                        )}
                    </div>
                )}

                {sourceLinks.length > 0 && <MessageSources sourceLinks={sourceLinks} />}

                {isLoading && (
                    <div
                        className={clsx(
                            'flex flex-wrap items-center justify-start gap-4 self-start',
                            message.content?.length && !isPretendLoading && 'py-3',
                        )}
                    >
                        <LoadingSpinner />
                        {isRealAiMessage(message) && <LoadingStatusMessage status={message.status} />}
                    </div>
                )}
            </TutorBotMessageContent>
            {shouldShowEvaluations && <MessageEvaluations messageId={message.id} />}
        </div>
    );
}
